import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { VscTrash, VscCopy, VscCheck } from 'react-icons/vsc';
import PrintButton from '../common/PrintButton';
import { getItems, createItem, updateItem, deleteItem } from '../../api/items';
import { getSwitchboards } from '../../api/switchboards';
import { getBreakers } from '../../api/breakers';
import { getSystems } from '../../api/systems';
import { getRooms } from '../../api/rooms';
import { getEmgStopGroups } from '../../api/emgStopGroups';
import SearchSelect from '../common/SearchSelect';
import ComboInput from '../common/ComboInput';
import RowNotices from '../common/RowNotices';
import { PhotoButton } from '../common/PhotoGallery';
import { useSort, sortRows, SortTh } from '../common/SortHeader';
import ColumnFilter from '../common/ColumnFilter';
import './Items.css';

const getItemVal = (item, key) => {
  switch (key) {
    case 'tag':          return item.tag || null;
    case 'description':  return item.description || null;
    case 'itemType':     return item.itemType || null;
    case 'room':         return item.room?.number ?? null;
    case 'switchboard':  return item.switchboard?.name || null;
    case 'breaker':      return item.breaker?.tag || null;
    case 'bkrType':      return item.breaker?.type || null;
    case 'setting':      return item.breaker?.setting || null;
    case 'system':       return item.system?.name || null;
    case 'service':      return item.service || null;
    case 'approved':     return item.approved ? 1 : 0;
    case 'consumer':     return item.consumer ? 1 : 0;
    case 'nelTrip':      return item.nelTrip ? 1 : 0;
    case 'power_kw':     return item.power_kw ?? null;
    case 'current_a':    return item.current_a ?? null;
    case 'voltage_v':    return item.voltage_v ?? null;
    case 'harbour':      return item.harbour ?? null;
    case 'fishing':      return item.fishing ?? null;
    case 'underWay':     return item.underWay ?? null;
    case 'offloading':   return item.offloading ?? null;
    case 'emergency':    return item.emergency ?? null;
    case 'emgStopGroup': return item.emgStopGroup?.name || null;
    case 'note':         return item.note || null;
    default:             return null;
  }
};

const SERVICE_OPTS = ['', 'Essential', 'Important', 'Non-essential', 'Emergency'];
const SERVICE_SS   = SERVICE_OPTS.filter(Boolean).map(s => ({ value: s, label: s }));
const SERVICE_CLS  = {
  Essential: 'badge badge-essential', Important: 'badge badge-important',
  'Non-essential': 'badge badge-noness', Emergency: 'badge badge-emergency',
};

const pctDisp  = (v) => (v == null || v === '') ? '—' : `${Math.round(v * 100)}%`;
const padRoom  = (n) => n != null ? String(n).padStart(3, '0') : '';

const ITEM_FILTER_TYPE = {
  tag: 'text', description: 'text', itemType: 'select', room: 'select',
  switchboard: 'select', breaker: 'text', bkrType: 'select', setting: 'text',
  system: 'select', service: 'select', approved: 'select', consumer: 'select',
  nelTrip: 'select', power_kw: 'text', current_a: 'text', voltage_v: 'text',
  harbour: 'text', fishing: 'text', underWay: 'text', offloading: 'text',
  emergency: 'text', emgStopGroup: 'select', note: 'text',
};
const ITEM_BOOL_COLS = new Set(['approved', 'consumer', 'nelTrip']);

const getItemFilterText = (i, key) => {
  switch (key) {
    case 'tag':          return i.tag || '';
    case 'description':  return i.description || '';
    case 'itemType':     return i.itemType || '';
    case 'room':         return i.room ? `${padRoom(i.room.number)} ${i.room.text}` : '';
    case 'switchboard':  return i.switchboard?.name || '';
    case 'breaker':      return i.breaker?.tag || '';
    case 'bkrType':      return i.breaker?.type || '';
    case 'setting':      return i.breaker?.setting || '';
    case 'system':       return i.system?.name || '';
    case 'service':      return i.service || '';
    case 'approved':     return i.approved ? '1' : '0';
    case 'consumer':     return i.consumer ? '1' : '0';
    case 'nelTrip':      return i.nelTrip ? '1' : '0';
    case 'power_kw':     return i.power_kw != null ? String(i.power_kw) : '';
    case 'current_a':    return i.current_a != null ? String(i.current_a) : '';
    case 'voltage_v':    return i.voltage_v != null ? String(i.voltage_v) : '';
    case 'harbour':      return i.harbour != null ? String(Math.round(i.harbour * 100)) : '';
    case 'fishing':      return i.fishing != null ? String(Math.round(i.fishing * 100)) : '';
    case 'underWay':     return i.underWay != null ? String(Math.round(i.underWay * 100)) : '';
    case 'offloading':   return i.offloading != null ? String(Math.round(i.offloading * 100)) : '';
    case 'emergency':    return i.emergency != null ? String(Math.round(i.emergency * 100)) : '';
    case 'emgStopGroup': return i.emgStopGroup?.name || '';
    case 'note':         return i.note || '';
    default:             return '';
  }
};

const itemToDraft = (item) => ({
  tag:          item.tag          || '',
  switchboard:  item.switchboard?._id  || '',
  breaker:      item.breaker?._id      || '',
  system:       item.system?._id       || '',
  room:         item.room?._id         || '',
  emgStopGroup: item.emgStopGroup?._id || '',
  itemType:     item.itemType    || '',
  description:  item.description || '',
  approved:     item.approved    ?? false,
  consumer:     item.consumer    ?? false,
  nelTrip:      item.nelTrip     ?? false,
  power_kw:     item.power_kw    ?? '',
  current_a:    item.current_a   ?? '',
  voltage_v:    item.voltage_v   ?? '',
  service:      item.service     || '',
  notices:      item.notices || [],
  harbour:      item.harbour     != null ? Math.round(item.harbour * 100)    : '',
  fishing:      item.fishing     != null ? Math.round(item.fishing * 100)    : '',
  underWay:     item.underWay    != null ? Math.round(item.underWay * 100)   : '',
  offloading:   item.offloading  != null ? Math.round(item.offloading * 100) : '',
  emergency:    item.emergency   != null ? Math.round(item.emergency * 100)  : '',
  note:         item.note || '',
});

const emptyDraft = () => ({
  tag: '', switchboard: '', breaker: '', system: '', room: '', emgStopGroup: '',
  itemType: '', description: '', approved: false, consumer: false, nelTrip: false,
  power_kw: '', current_a: '', voltage_v: '', service: '',
  harbour: '', fishing: '', underWay: '', offloading: '', emergency: '', note: '',
  notices: [],
});

const draftToPayload = (d, projectId) => ({
  project:      projectId,
  tag:          d.tag          || undefined,
  switchboard:  d.switchboard  || undefined,
  breaker:      d.breaker      || undefined,
  system:       d.system       || undefined,
  room:         d.room         || undefined,
  emgStopGroup: d.emgStopGroup || undefined,
  itemType:     d.itemType     || undefined,
  description:  d.description  || undefined,
  approved:     d.approved,
  consumer:     d.consumer,
  nelTrip:      d.nelTrip,
  power_kw:     d.power_kw     !== '' ? parseFloat(d.power_kw)    : undefined,
  current_a:    d.current_a    !== '' ? parseFloat(d.current_a)   : undefined,
  voltage_v:    d.voltage_v    !== '' ? parseFloat(d.voltage_v)   : undefined,
  service:      d.service      || undefined,
  harbour:      d.harbour      !== '' ? parseFloat(d.harbour)    / 100 : undefined,
  fishing:      d.fishing      !== '' ? parseFloat(d.fishing)    / 100 : undefined,
  underWay:     d.underWay     !== '' ? parseFloat(d.underWay)   / 100 : undefined,
  offloading:   d.offloading   !== '' ? parseFloat(d.offloading) / 100 : undefined,
  emergency:    d.emergency    !== '' ? parseFloat(d.emergency)  / 100 : undefined,
  note:         d.note         || undefined,
  notices:      d.notices,
});

// Navigate between data-cell <td>s. Calls { save, cancel, up, down } callbacks for special keys.
const rowNav = (e, trEl, { save, cancel, up, down }) => {
  const cells   = [...trEl.querySelectorAll('td[data-cell]')];
  const curCell = cells.find(td => td.contains(document.activeElement));
  const idx     = cells.indexOf(curCell);
  const goTo    = (i) => { if (i < 0 || i >= cells.length) return false; cells[i].querySelector('.ss-trigger, input, select')?.focus(); return true; };

  if (e.key === 'F2') {
    e.preventDefault();
    const el = e.target;
    if (el.type === 'number') { const v = el.value; el.value = ''; el.value = v; }
    else { try { const len = el.value?.length ?? 0; el.setSelectionRange(len, len); } catch {} }
    return;
  }
  if (e.key === 'ArrowUp')   { e.preventDefault(); up?.(idx); return; }
  if (e.key === 'ArrowDown') { e.preventDefault(); down?.(idx); return; }
  if (e.key === 'Enter')     { if (e.target.tagName === 'SELECT') return; e.preventDefault(); if (down) down(idx); else save(); return; }
  if (e.key === 'Tab') {
    e.preventDefault();
    if (e.shiftKey) { if (!goTo(idx - 1) && up) up(cells.length - 1); }
    else { if (!goTo(idx + 1)) { if (down) down(0); else save(); } }
    return;
  }
  if (e.key === 'Escape') { e.preventDefault(); return; }
  if (e.key === 'ArrowRight') {
    const el = document.activeElement;
    if (el && el.tagName === 'INPUT' && el.type !== 'number' && el.selectionStart !== null && el.selectionEnd < el.value.length) return;
    e.preventDefault(); cells[idx + 1]?.querySelector('.ss-trigger, input, select')?.focus(); return;
  }
  if (e.key === 'ArrowLeft') {
    const el = document.activeElement;
    if (el && el.tagName === 'INPUT' && el.type !== 'number' && el.selectionStart !== null && el.selectionStart > 0) return;
    e.preventDefault(); cells[idx - 1]?.querySelector('.ss-trigger, input, select')?.focus(); return;
  }
};

// kW / A are shown with at most two decimals (the stored value stays exact)
const round2 = (v) => (v == null || v === '' ? null : Math.round(Number(v) * 100) / 100);

function ItemList({ project, isActive, role, highlightTag, onOpenTab }) {
  const [items, setItems]         = useState([]);
  const [lookups, setLookups]     = useState({ switchboards: [], breakers: [], systems: [], rooms: [], emgGroups: [] });
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft]         = useState(null);
  const [saveErr, setSaveErr]     = useState('');
  const [newRow, setNewRow]       = useState(emptyDraft);
  const [newErr, setNewErr]       = useState('');
  const [search, setSearch]       = useState(() => sessionStorage.getItem(`item-search-${project?._id}`) || '');
  const [sort, toggleSort]        = useSort('description');
  const sortInitRef = useRef(false);
  const [copiedId, setCopiedId]   = useState(null);
  const [filters, setFilters]     = useState({});
  const newRowRef  = useRef(null);
  const editRowRef      = useRef(null);
  const tableRef        = useRef(null);
  const saveTimer       = useRef(null);  // debounce timer for existing-row auto-save
  const pendingFocusCol = useRef(0);
  const undoRef      = useRef([]);
  const redoRef      = useRef([]);
  const beforeMapRef = useRef({});
  const undoRedoRef  = useRef(null);
  const lastFocusRef   = useRef(null);
  const pendingRestore = useRef(false);
  const pendingEditRef = useRef(null);

  const canEdit  = ['admin','engineer','manager','foreman'].includes(role);
  const canPrint = ['admin','engineer','manager','foreman','team-leader'].includes(role);

  useEffect(() => {
    if (highlightTag) setSearch(highlightTag);
  }, [highlightTag]);

  useEffect(() => {
    if (project?._id) sessionStorage.setItem(`item-search-${project._id}`, search);
  }, [search, project?._id]);

  const setPhotos = (id) => (photos) =>
    setItems(prev => prev.map(i => i._id === id ? { ...i, photos } : i));

  const photoBtn = (item) => (
    <PhotoButton kind="items" record={item} title={item.tag || item.description || 'Item'}
      role={role} onPhotosChange={setPhotos(item._id)} />
  );

  const handleCopy = (item) => {
    const nbNo = project?.newbuildingNo;
    const text = nbNo ? `#${nbNo}-${item.tag}` : `#${item.tag}`;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(item._id);
      setTimeout(() => setCopiedId(null), 1500);
    }).catch(() => {});
  };

  useEffect(() => {
    if (!project || !isActive) return;
    Promise.all([
      getItems(project._id),
      getSwitchboards(project._id),
      getBreakers(project._id),
      getSystems(project._id),
      getRooms(project._id),
      getEmgStopGroups(project._id),
    ]).then(([it, sb, br, sy, ro, eg]) => {
      setItems(sortRows(it.data, sort, getItemVal));
      sortInitRef.current = true;
      setLookups({ switchboards: sb.data, breakers: br.data, systems: sy.data, rooms: ro.data, emgGroups: eg.data });
    }).catch(() => {});
  }, [project?._id, isActive]);

  useEffect(() => {
    if (!sortInitRef.current) return;
    setItems(prev => sortRows(prev, sort, getItemVal));
  }, [sort.key, sort.dir]);

  useEffect(() => {
    if (!isActive) return;
    const onKeyDown = async (e) => {
      if (!e.ctrlKey || (e.key !== 'z' && e.key !== 'y')) return;
      const el = document.activeElement;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') && !el.closest('.module-wrap')) return;
      e.preventDefault();
      if (e.key === 'z') await undoRedoRef.current?.undo();
      else await undoRedoRef.current?.redo();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isActive]);

  const trackFocus = (e) => {
    const tr = e.target.closest('tr');
    if (!tr) return;
    const rowId = tr.dataset.rowId || (tr === newRowRef.current ? '_new' : editingId);
    if (!rowId) return;
    const cells = [...tr.querySelectorAll('td[data-cell]')];
    const colIdx = cells.findIndex(td => td.contains(e.target));
    lastFocusRef.current = { rowId, colIdx: Math.max(0, colIdx) };
  };

  useEffect(() => {
    if (isActive) pendingRestore.current = true;
  }, [isActive]);

  useLayoutEffect(() => {
    if (!canEdit) return;
    if (pendingRestore.current && sorted.length) {
      pendingRestore.current = false;
      const lf = lastFocusRef.current;
      if (editingId) {
        setTimeout(() => {
          const cells = [...(editRowRef.current?.querySelectorAll('td[data-cell]') || [])];
          const col = lf?.colIdx || 0;
          (cells[col] || cells[0])?.querySelector('.ss-trigger, input, select')?.focus();
        }, 0);
        return;
      }
      if (lf && lf.rowId === '_new') {
        setTimeout(() => {
          const cells = [...(newRowRef.current?.querySelectorAll('td[data-cell]') || [])];
          (cells[lf.colIdx] || cells[0])?.querySelector('.ss-trigger, input, select')?.focus();
        }, 0);
        return;
      }
      const row = lf ? sorted.find(i => i._id === lf.rowId) : null;
      startEdit(row || sorted[0], lf?.colIdx || 0);
      return;
    }
    if (pendingEditRef.current && !editingId && sorted.length) {
      const { id, colIdx } = pendingEditRef.current;
      pendingEditRef.current = null;
      const row = sorted.find(i => i._id === id);
      if (row) startEdit(row, colIdx);
      else if (sorted.length) startEdit(sorted[0], 0);
    }
  });

  // Auto-save an existing row after 500 ms of no changes
  const autoSave = (d, id) => {
    if (!id || id === '_new' || !project) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const res = await updateItem(id, draftToPayload(d, project._id));
        setItems(prev => prev.map(i => i._id === id ? res.data : i));
      } catch (e) { setSaveErr(e.response?.data?.msg || 'Save failed.'); }
    }, 500);
  };

  const pushHistory = (id, before, after) => {
    if (!before || JSON.stringify(before) === JSON.stringify(after)) return;
    undoRef.current = [{ id, before, after }, ...undoRef.current].slice(0, 30);
    redoRef.current = [];
  };

  // Immediate save (cancel any debounce first)
  const flushSave = async (d, id) => {
    clearTimeout(saveTimer.current);
    if (!d || !id || id === '_new' || !project) return;
    try {
      const res = await updateItem(id, draftToPayload(d, project._id));
      setItems(prev => prev.map(i => i._id === id ? res.data : i));
    } catch (e) { setSaveErr(e.response?.data?.msg || 'Save failed.'); }
  };

  const setField = (k) => (v) => {
    setDraft(prev => {
      const next = { ...prev, [k]: v };
      if (k === 'switchboard') next.breaker = '';
      autoSave(next, editingId);
      return next;
    });
  };

  // Focus the correct column whenever the edit row changes
  useLayoutEffect(() => {
    if (!editingId) return;
    const focusCol = () => {
      if (!editRowRef.current) return false;
      const cells = [...editRowRef.current.querySelectorAll('td[data-cell]')];
      const el = (cells[pendingFocusCol.current] ?? cells[0])?.querySelector('.ss-trigger, input, select');
      if (el) { el.focus(); return true; }
      return false;
    };
    if (!focusCol()) setTimeout(focusCol, 0);
  }, [editingId]);

  // Open a row for editing; fire-and-forget save of the previous row
  const startEdit = (item, colIdx = 0) => {
    if (!canEdit) return;
    clearTimeout(saveTimer.current);
    if (editingId && editingId !== '_new' && draft) {
      pushHistory(editingId, beforeMapRef.current[editingId], draft);
      delete beforeMapRef.current[editingId];
      flushSave(draft, editingId);
    }
    beforeMapRef.current[item._id] = itemToDraft(item);
    pendingFocusCol.current = colIdx;
    setEditingId(item._id);
    setDraft(itemToDraft(item));
    setSaveErr('');
  };

  const cancelEdit = (itemId) => {
    clearTimeout(saveTimer.current);
    delete beforeMapRef.current[itemId];
    setEditingId(null); setDraft(null); setSaveErr('');
    setTimeout(() => document.querySelector(`tr[data-row-id="${itemId}"]`)?.focus(), 0);
  };

  // Enter / Tab-past-last on existing row: flush + exit edit mode
  const commitEdit = async () => {
    clearTimeout(saveTimer.current);
    const id = editingId;
    if (draft && id) {
      pushHistory(id, beforeMapRef.current[id], draft);
      delete beforeMapRef.current[id];
      await flushSave(draft, id);
    }
    setEditingId(null); setDraft(null);
    if (id) setTimeout(() => document.querySelector(`tr[data-row-id="${id}"]`)?.focus(), 0);
  };

  // Save the new bottom row (only if it has data)
  const saveNew = async () => {
    const hasData = Object.entries(newRow).some(([k, v]) =>
      typeof v !== 'boolean' && !Array.isArray(v) && v !== '' && v != null);
    if (!hasData) return;
    setNewErr('');
    try {
      const res = await createItem(draftToPayload(newRow, project._id));
      setItems(prev => [...prev, res.data]);
      setNewRow(emptyDraft());
      setTimeout(() => newRowRef.current?.querySelector('.ss-trigger, input, select')?.focus(), 0);
    } catch (e) { setNewErr(e.response?.data?.msg || 'Save failed.'); }
  };

  const setNewField = (k) => (v) => setNewRow(d => { const n = { ...d, [k]: v }; if (k === 'switchboard') n.breaker = ''; return n; });

  const handleNavRow = (dir, currentId, colIdx = 0) => {
    clearTimeout(saveTimer.current);
    const isNew = currentId === '_new';
    if (!isNew && draft) {
      pushHistory(currentId, beforeMapRef.current[currentId], draft);
      delete beforeMapRef.current[currentId];
      flushSave(draft, currentId);
    } else if (isNew) saveNew();

    if (isNew) {
      if (dir === 'up' && sorted.length > 0) {
        const t = sorted[sorted.length - 1];
        beforeMapRef.current[t._id] = itemToDraft(t);
        pendingFocusCol.current = colIdx;
        setEditingId(t._id); setDraft(itemToDraft(t));
      }
      return; // can't go down from new row
    }

    const idx = sorted.findIndex(i => i._id === currentId);
    if (dir === 'up') {
      if (idx > 0) {
        beforeMapRef.current[sorted[idx-1]._id] = itemToDraft(sorted[idx-1]);
        pendingFocusCol.current = colIdx;
        setEditingId(sorted[idx-1]._id); setDraft(itemToDraft(sorted[idx-1]));
      }
      // else: already at top — stay
    } else {
      if (idx < sorted.length - 1) {
        beforeMapRef.current[sorted[idx+1]._id] = itemToDraft(sorted[idx+1]);
        pendingFocusCol.current = colIdx;
        setEditingId(sorted[idx+1]._id); setDraft(itemToDraft(sorted[idx+1]));
      } else {
        setEditingId(null); setDraft(null);
        setTimeout(() => {
          const cells = [...(newRowRef.current?.querySelectorAll('td[data-cell]') || [])];
          ((cells[colIdx] ?? cells[0])?.querySelector('.ss-trigger, input, select'))?.focus();
        }, 0);
      }
    }
  };

  const removeItem = async (id) => {
    if (!confirm('Delete this item?')) return;
    clearTimeout(saveTimer.current);
    const idx = sorted.findIndex(i => i._id === id);
    const neighbor = sorted[idx + 1] || sorted[idx - 1];
    try {
      await deleteItem(id);
      setItems(prev => prev.filter(i => i._id !== id));
      if (editingId === id) { setEditingId(null); setDraft(null); setSaveErr(''); }
      if (neighbor) pendingEditRef.current = { id: neighbor._id, colIdx: 0 };
    }
    catch { setSaveErr('Delete failed.'); }
  };

  const toggleCheck = async (item, field, e) => {
    e.stopPropagation(); if (!canEdit) return;
    try { const res = await updateItem(item._id, { [field]: !item[field] }); setItems(prev => prev.map(i => i._id === item._id ? res.data : i)); }
    catch {}
  };

  const brkOptsFor = (d) => {
    const list = d?.switchboard
      ? lookups.breakers.filter(b => (b.switchboard?._id || b.switchboard) === d.switchboard)
      : lookups.breakers;
    return list.map(b => ({ value: b._id, label: b.tag + (b.type ? ` — ${b.type}` : '') }));
  };

  const sbOpts  = lookups.switchboards.map(s => ({ value: s._id, label: s.tag ? `${s.name} (${s.tag})` : s.name }));
  const sysOpts = lookups.systems.map(s => ({ value: s._id, label: s.name }));
  const rmOpts  = lookups.rooms.map(r => ({ value: r._id, label: `${padRoom(r.number)} — ${r.text}` }));
  const egOpts  = lookups.emgGroups.map(g => ({ value: g._id, label: g.name + (g.text ? `: ${g.text}` : '') }));

  const typeSuggestions = [...new Set(items.map(i => i.itemType).filter(Boolean))].sort();

  const filterOpts = useMemo(() => {
    const opts = {};
    for (const [key, type] of Object.entries(ITEM_FILTER_TYPE)) {
      if (type !== 'select') continue;
      const vals = new Set();
      items.forEach(i => vals.add(getItemFilterText(i, key)));
      const isBool = ITEM_BOOL_COLS.has(key);
      opts[key] = [...vals].sort().map(v => ({
        value: v,
        label: isBool ? (v === '1' ? 'Yes' : 'No') : (v || '(Empty)'),
      }));
    }
    return opts;
  }, [items]);

  const setFilter = (key, val) => setFilters(prev => {
    const next = { ...prev };
    if (val === undefined || val === '' || val === null) delete next[key];
    else next[key] = val;
    return next;
  });

  const cf = (key) => {
    const type = ITEM_FILTER_TYPE[key];
    if (!type) return undefined;
    return <ColumnFilter type={type} value={filters[key]} options={type === 'select' ? filterOpts[key] : undefined} onChange={v => setFilter(key, v)} />;
  };

  const hasFilters = Object.keys(filters).length > 0;

  const sorted = items.filter(i => {
    if (search) {
      const q = search.toLowerCase();
      if (!((i.tag || '').toLowerCase().includes(q) || (i.description || '').toLowerCase().includes(q) ||
            (i.itemType || '').toLowerCase().includes(q) || (i.breaker?.tag || '').toLowerCase().includes(q) ||
            (i.room ? `${padRoom(i.room.number)} ${i.room.text}` : '').toLowerCase().includes(q) ||
            (i.system?.name || '').toLowerCase().includes(q))) return false;
    }
    for (const [key, filter] of Object.entries(filters)) {
      const val = getItemFilterText(i, key);
      if (typeof filter === 'string') {
        if (!val.toLowerCase().includes(filter.toLowerCase())) return false;
      } else if (filter instanceof Set) {
        if (!filter.has(val)) return false;
      }
    }
    return true;
  });

  undoRedoRef.current = {
    undo: async () => {
      if (!undoRef.current.length) return;
      const [entry, ...rest] = undoRef.current;
      undoRef.current = rest;
      redoRef.current = [entry, ...redoRef.current].slice(0, 30);
      if (editingId === entry.id) { setEditingId(null); setDraft(null); }
      await flushSave(entry.before, entry.id);
      pendingEditRef.current = { id: entry.id, colIdx: 0 };
    },
    redo: async () => {
      if (!redoRef.current.length) return;
      const [entry, ...rest] = redoRef.current;
      redoRef.current = rest;
      undoRef.current = [entry, ...undoRef.current].slice(0, 30);
      if (editingId === entry.id) { setEditingId(null); setDraft(null); }
      await flushSave(entry.after, entry.id);
      pendingEditRef.current = { id: entry.id, colIdx: 0 };
    },
  };

  if (!project) return <div className="empty-state">Select a project first.</div>;

  const summary = sorted.reduce((s, i) => ({
    count:      s.count + 1,
    power_kw:   s.power_kw   + (i.power_kw  || 0),
    current_a:  s.current_a  + (i.current_a || 0),
  }), { count: 0, power_kw: 0, current_a: 0 });

  const sel = e => e.target.select();

  const renderEditCells = (d, setF, bOpts, bSel) => (
    <>
      <td data-cell><input value={d.tag} onChange={e => setF('tag')(e.target.value)} onFocus={sel} placeholder="Tag" className="cell-input-mono" /></td>
      <td data-cell><input value={d.description} onChange={e => setF('description')(e.target.value)} onFocus={sel} placeholder="Description" className="cell-input-wide" /></td>
      <td data-cell><ComboInput value={d.itemType} onChange={setF('itemType')} suggestions={typeSuggestions} placeholder="Type…" /></td>
      <td data-cell><SearchSelect options={rmOpts}  value={d.room}        onChange={setF('room')} /></td>
      <td data-cell><SearchSelect options={sbOpts}  value={d.switchboard} onChange={setF('switchboard')} /></td>
      <td data-cell><SearchSelect options={bOpts}   value={d.breaker}     onChange={setF('breaker')} disabled={!d.switchboard} /></td>
      <td className="cell-ro">{bSel?.type || '—'}</td>
      <td className="cell-ro">{bSel?.setting || '—'}</td>
      <td data-cell><SearchSelect options={sysOpts} value={d.system}      onChange={setF('system')} /></td>
      <td data-cell><SearchSelect options={SERVICE_SS} value={d.service} onChange={setF('service')} /></td>
      <td data-cell className="cell-check"><input type="checkbox" checked={d.approved}  onChange={e => setF('approved')(e.target.checked)}  /></td>
      <td data-cell className="cell-check"><input type="checkbox" checked={d.consumer}  onChange={e => setF('consumer')(e.target.checked)}  /></td>
      <td data-cell className="cell-check"><input type="checkbox" checked={d.nelTrip}   onChange={e => setF('nelTrip')(e.target.checked)}   /></td>
      <td data-cell><input type="number" value={d.power_kw}   onChange={e => setF('power_kw')(e.target.value)}   onFocus={sel} className="num-input" /></td>
      <td data-cell><input type="number" value={d.current_a}  onChange={e => setF('current_a')(e.target.value)}  onFocus={sel} className="num-input" /></td>
      <td data-cell><input type="number" value={d.voltage_v}  onChange={e => setF('voltage_v')(e.target.value)}  onFocus={sel} className="num-input" /></td>
      <td data-cell><input type="number" min="0" max="100" value={d.harbour}    onChange={e => setF('harbour')(e.target.value)}    onFocus={sel} className="pct-input" /></td>
      <td data-cell><input type="number" min="0" max="100" value={d.fishing}    onChange={e => setF('fishing')(e.target.value)}    onFocus={sel} className="pct-input" /></td>
      <td data-cell><input type="number" min="0" max="100" value={d.underWay}   onChange={e => setF('underWay')(e.target.value)}   onFocus={sel} className="pct-input" /></td>
      <td data-cell><input type="number" min="0" max="100" value={d.offloading} onChange={e => setF('offloading')(e.target.value)} onFocus={sel} className="pct-input" /></td>
      <td data-cell><input type="number" min="0" max="100" value={d.emergency}  onChange={e => setF('emergency')(e.target.value)}  onFocus={sel} className="pct-input" /></td>
      <td data-cell><SearchSelect options={egOpts}  value={d.emgStopGroup} onChange={setF('emgStopGroup')} /></td>
      <td data-cell><input value={d.note} onChange={e => setF('note')(e.target.value)} onFocus={sel} placeholder="Note" className="cell-input-wide" /></td>
      <td data-cell style={{ position: 'relative' }}>
        <RowNotices notices={d.notices} editing onChange={v => setF('notices')(v)} />
      </td>
    </>
  );

  return (
    <div className="module-wrap">
      <div className="module-toolbar">
        <span className="module-toolbar-title">Item List</span>
        <input className="module-search" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} />
        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{sorted.length} items</span>
        {hasFilters && <button className="btn-link" style={{ fontSize: 11 }} onClick={() => setFilters({})}>Clear filters</button>}
        {canPrint && <PrintButton
          title={`Item List — ${project?.name ?? ''}`}
          storageKey="items"
          rows={sorted}
          onOpenTab={onOpenTab}
          columns={[
            { key: 'tag',         label: 'Tag',         render: i => i.tag || '' },
            { key: 'description', label: 'Description', render: i => i.description || '' },
            { key: 'itemType',    label: 'Type',        render: i => i.itemType || '' },
            { key: 'room',        label: 'Room',        render: i => i.room ? `${padRoom(i.room.number)} ${i.room.text}` : '' },
            { key: 'switchboard', label: 'Switchboard', render: i => i.switchboard?.name || '' },
            { key: 'breaker',     label: 'Breaker',     render: i => i.breaker?.tag || '' },
            { key: 'bkrType',     label: 'Bkr Type',   render: i => i.breaker?.type || '', defaultOn: false },
            { key: 'setting',     label: 'Setting',     render: i => i.breaker?.setting || '', defaultOn: false },
            { key: 'system',      label: 'System',      render: i => i.system?.name || '' },
            { key: 'service',     label: 'Service',     render: i => i.service || '' },
            { key: 'approved',    label: 'App.',        render: i => i.approved ? '✓' : '', defaultOn: false },
            { key: 'consumer',    label: 'Con.',        render: i => i.consumer ? '✓' : '', defaultOn: false },
            { key: 'nelTrip',     label: 'NEL',         render: i => i.nelTrip  ? '✓' : '', defaultOn: false },
            { key: 'power_kw',    label: 'kW',          render: i => round2(i.power_kw)  ?? '' },
            { key: 'current_a',   label: 'A',           render: i => round2(i.current_a) ?? '' },
            { key: 'voltage_v',   label: 'V',           render: i => i.voltage_v ?? '', defaultOn: false },
            { key: 'harbour',     label: 'Harb%',       render: i => pctDisp(i.harbour),    defaultOn: false },
            { key: 'fishing',     label: 'Fish%',       render: i => pctDisp(i.fishing),    defaultOn: false },
            { key: 'underWay',    label: 'U/W%',        render: i => pctDisp(i.underWay),   defaultOn: false },
            { key: 'offloading',  label: 'Off%',        render: i => pctDisp(i.offloading), defaultOn: false },
            { key: 'emergency',   label: 'Emg%',        render: i => pctDisp(i.emergency) },
            { key: 'emgGroup',    label: 'EMG Grp',     render: i => i.emgStopGroup?.name || '' },
            { key: 'note',        label: 'Note',        render: i => i.note || '', defaultOn: false },
          ]}
        />}
      </div>
      {(saveErr || newErr) && <div className="row-error">{saveErr || newErr}</div>}
      <div className="module-body">
        <table ref={tableRef} tabIndex={-1} className="data-table item-table" style={{ outline: 'none' }} onFocusCapture={trackFocus}>
          <colgroup>
            <col style={{ width: 100 }} />{/* Tag */}
            <col style={{ width: 180 }} />{/* Description */}
            <col style={{ width: 110 }} />{/* Type */}
            <col style={{ width: 130 }} />{/* Room */}
            <col style={{ width: 140 }} />{/* Switchboard */}
            <col style={{ width: 110 }} />{/* Breaker */}
            <col style={{ width: 80  }} />{/* Bkr Type */}
            <col style={{ width: 68  }} />{/* Setting */}
            <col style={{ width: 110 }} />{/* System */}
            <col style={{ width: 100 }} />{/* Service */}
            <col style={{ width: 42  }} />{/* App. */}
            <col style={{ width: 42  }} />{/* Con. */}
            <col style={{ width: 42  }} />{/* NEL */}
            <col style={{ width: 70  }} />{/* kW */}
            <col style={{ width: 70  }} />{/* A */}
            <col style={{ width: 70  }} />{/* V */}
            <col style={{ width: 54  }} />{/* Harb% */}
            <col style={{ width: 54  }} />{/* Fish% */}
            <col style={{ width: 54  }} />{/* U/W% */}
            <col style={{ width: 54  }} />{/* Off% */}
            <col style={{ width: 54  }} />{/* Emg% */}
            <col style={{ width: 130 }} />{/* EMG Grp */}
            <col style={{ width: 160 }} />{/* Note */}
            <col style={{ width: 70 }} />{/* Notices */}
            <col style={{ width: 56 }} />{/* Photos + Copy */}
            {canEdit && <col style={{ width: 36 }} />}{/* Delete */}
          </colgroup>
          <thead>
            <tr>
              <SortTh sortKey="tag"           sort={sort} onSort={toggleSort} filter={cf('tag')}>Tag</SortTh>
              <SortTh sortKey="description"  sort={sort} onSort={toggleSort} filter={cf('description')}>Description</SortTh>
              <SortTh sortKey="itemType"     sort={sort} onSort={toggleSort} filter={cf('itemType')}>Type</SortTh>
              <SortTh sortKey="room"         sort={sort} onSort={toggleSort} filter={cf('room')}>Room</SortTh>
              <SortTh sortKey="switchboard"  sort={sort} onSort={toggleSort} filter={cf('switchboard')}>Switchboard</SortTh>
              <SortTh sortKey="breaker"      sort={sort} onSort={toggleSort} filter={cf('breaker')}>Breaker</SortTh>
              <SortTh sortKey="bkrType"      sort={sort} onSort={toggleSort} filter={cf('bkrType')}>Bkr Type</SortTh>
              <SortTh sortKey="setting"      sort={sort} onSort={toggleSort} filter={cf('setting')}>Setting</SortTh>
              <SortTh sortKey="system"       sort={sort} onSort={toggleSort} filter={cf('system')}>System</SortTh>
              <SortTh sortKey="service"      sort={sort} onSort={toggleSort} filter={cf('service')}>Service</SortTh>
              <SortTh sortKey="approved"     sort={sort} onSort={toggleSort} filter={cf('approved')} title="Approved">App.</SortTh>
              <SortTh sortKey="consumer"     sort={sort} onSort={toggleSort} filter={cf('consumer')} title="Consumer">Con.</SortTh>
              <SortTh sortKey="nelTrip"      sort={sort} onSort={toggleSort} filter={cf('nelTrip')} title="NEL Trip">NEL</SortTh>
              <SortTh sortKey="power_kw"     sort={sort} onSort={toggleSort} filter={cf('power_kw')}>kW</SortTh>
              <SortTh sortKey="current_a"    sort={sort} onSort={toggleSort} filter={cf('current_a')}>A</SortTh>
              <SortTh sortKey="voltage_v"    sort={sort} onSort={toggleSort} filter={cf('voltage_v')}>V</SortTh>
              <SortTh sortKey="harbour"      sort={sort} onSort={toggleSort} filter={cf('harbour')} title="Harbour %">Harb%</SortTh>
              <SortTh sortKey="fishing"      sort={sort} onSort={toggleSort} filter={cf('fishing')} title="Fishing %">Fish%</SortTh>
              <SortTh sortKey="underWay"     sort={sort} onSort={toggleSort} filter={cf('underWay')} title="Under Way %">U/W%</SortTh>
              <SortTh sortKey="offloading"   sort={sort} onSort={toggleSort} filter={cf('offloading')} title="Offloading %">Off%</SortTh>
              <SortTh sortKey="emergency"    sort={sort} onSort={toggleSort} filter={cf('emergency')} title="Emergency %">Emg%</SortTh>
              <SortTh sortKey="emgStopGroup" sort={sort} onSort={toggleSort} filter={cf('emgStopGroup')}>EMG Grp</SortTh>
              <SortTh sortKey="note"         sort={sort} onSort={toggleSort} filter={cf('note')}>Note</SortTh>
              <th>Notices</th>
              <th style={{ width: 56 }}></th>
              {canEdit && <th style={{ width: 32 }}></th>}
            </tr>
          </thead>
          <tbody>
            {sorted.map((item, rowIdx) => {
              if (editingId === item._id) {
                const bOpts = brkOptsFor(draft);
                const bSel  = lookups.breakers.find(b => b._id === draft.breaker);
                return (
                  <tr key={item._id} ref={editRowRef} className="row-editing"
                    onKeyDown={e => rowNav(e, e.currentTarget, {
                      save:   commitEdit,
                      cancel: () => cancelEdit(item._id),
                      up:     (ci) => handleNavRow('up',   item._id, ci),
                      down:   (ci) => handleNavRow('down', item._id, ci),
                    })}>
                    {renderEditCells(draft, setField, bOpts, bSel)}
                    <td className="cell-actions" onClick={e => e.stopPropagation()}>{photoBtn(item)}</td>
                    <td className="cell-actions" onClick={e => e.stopPropagation()}>
                      <button className="btn-icon btn-icon-danger" title="Delete" onClick={() => removeItem(item._id)}><VscTrash size={13} /></button>
                    </td>
                  </tr>
                );
              }
              return (
                <tr key={item._id}
                  data-row-id={item._id}
                  tabIndex={canEdit ? -1 : undefined}
                  className={canEdit ? 'data-row-click' : ''}
                  onClick={e => {
                    const cells = [...e.currentTarget.querySelectorAll('td[data-cell]')];
                    const clicked = cells.findIndex(td => td.contains(e.target));
                    startEdit(item, Math.max(0, clicked));
                  }}
                  onKeyDown={!canEdit ? undefined : e => {
                    if (e.key === 'Escape') { e.preventDefault(); return; }
                    e.preventDefault();
                    if (e.key === 'ArrowDown') {
                      const idx = sorted.findIndex(i => i._id === item._id);
                      if (idx < sorted.length - 1) startEdit(sorted[idx + 1], 0);
                      else newRowRef.current?.querySelector('.ss-trigger, input, select')?.focus();
                    } else if (e.key === 'ArrowUp') {
                      const idx = sorted.findIndex(i => i._id === item._id);
                      if (idx > 0) startEdit(sorted[idx - 1], 0);
                    } else if (!e.ctrlKey && !e.metaKey && !e.altKey) {
                      startEdit(item, 0);
                    }
                  }}>
                  <td className="cell-input-mono">{item.tag || '—'}</td>
                  <td>{item.description || '—'}</td>
                  <td>{item.itemType || '—'}</td>
                  <td>{item.room ? `${padRoom(item.room.number)} ${item.room.text}` : '—'}</td>
                  <td>{item.switchboard?.name || '—'}</td>
                  <td>{item.breaker?.tag || '—'}</td>
                  <td>{item.breaker?.type || '—'}</td>
                  <td>{item.breaker?.setting || '—'}</td>
                  <td>{item.system?.name || '—'}</td>
                  <td>{item.service ? <span className={SERVICE_CLS[item.service]}>{item.service}</span> : '—'}</td>
                  <td className="cell-check" onClick={e => toggleCheck(item, 'approved', e)}><input type="checkbox" checked={!!item.approved} readOnly /></td>
                  <td className="cell-check" onClick={e => toggleCheck(item, 'consumer', e)}><input type="checkbox" checked={!!item.consumer} readOnly /></td>
                  <td className="cell-check" onClick={e => toggleCheck(item, 'nelTrip',  e)}><input type="checkbox" checked={!!item.nelTrip}  readOnly /></td>
                  <td className="cell-num">{round2(item.power_kw)  ?? '—'}</td>
                  <td className="cell-num">{round2(item.current_a) ?? '—'}</td>
                  <td className="cell-num">{item.voltage_v ?? '—'}</td>
                  <td className="cell-num">{pctDisp(item.harbour)}</td>
                  <td className="cell-num">{pctDisp(item.fishing)}</td>
                  <td className="cell-num">{pctDisp(item.underWay)}</td>
                  <td className="cell-num">{pctDisp(item.offloading)}</td>
                  <td className="cell-num">{pctDisp(item.emergency)}</td>
                  <td>{item.emgStopGroup?.name || '—'}</td>
                  <td className="cell-note">{item.note || '—'}</td>
                  <td><RowNotices notices={item.notices || []} /></td>
                  <td className="cell-actions" onClick={e => e.stopPropagation()}>
                    {photoBtn(item)}
                    {role !== 'client' && (
                      <button className="btn-icon" title="Copy chat link" onClick={() => handleCopy(item)}>
                        {copiedId === item._id ? <VscCheck size={12} /> : <VscCopy size={12} />}
                      </button>
                    )}
                  </td>
                  {canEdit && <td />}
                </tr>
              );
            })}
            {/* Always-present empty row at the bottom, above totals */}
            {canEdit && (() => {
              const bOpts = brkOptsFor(newRow);
              const bSel  = lookups.breakers.find(b => b._id === newRow.breaker);
              return (
                <tr ref={newRowRef} className="row-editing row-new"
                  onKeyDown={e => rowNav(e, e.currentTarget, {
                    save:   saveNew,
                    cancel: () => setNewRow(emptyDraft()),
                    up:     (ci) => handleNavRow('up',   '_new', ci),
                    down:   null,
                  })}>
                  {renderEditCells(newRow, setNewField, bOpts, bSel)}
                  <td /><td />
                </tr>
              );
            })()}
            {sorted.length > 0 && (
              <tr className="row-summary">
                <td colSpan={13}><span className="summary-label">TOTAL · {summary.count} items</span></td>
                <td className="cell-num summary-val">{summary.power_kw ? summary.power_kw.toFixed(1) : '—'}</td>
                <td className="cell-num summary-val">{summary.current_a ? summary.current_a.toFixed(1) : '—'}</td>
                <td />
                <td /><td /><td /><td /><td />
                <td /><td /><td />{canEdit && <td />}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ItemList;
